import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useStoreSelectionStore } from "@/stores/store-selection-store";
import { useShallow } from "zustand/react/shallow";

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  isScrolled?: boolean;
}

export default function MobileMenu({
  isOpen,
  onClose,
  isScrolled,
}: MobileMenuProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const lastPathRef = useRef(location.pathname + location.search);
  // Captures scroll position at the moment the menu opens so we can
  // restore it when the body lock is released on close.
  const scrollYRef = useRef(0);

  // Close on navigation
  useEffect(() => {
    const currentPath = location.pathname + location.search;
    if (isOpen && currentPath !== lastPathRef.current) {
      onClose();
    }
    lastPathRef.current = currentPath;
  }, [location.pathname, location.search, isOpen, onClose]);

  // Body scroll lock — Safari iOS does not stop body scrolling behind a
  // fixed overlay without this. The position:fixed + top trick preserves
  // the scroll offset so the page doesn't jump when the menu closes.
  useEffect(() => {
    if (isOpen) {
      scrollYRef.current = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollYRef.current}px`;
      document.body.style.width = "100%";
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      window.scrollTo(0, scrollYRef.current);
    }

    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const { storeId, storeName, service } = useStoreSelectionStore(
    useShallow((s) => ({
      storeId: s.storeId || "",
      storeName: s.storeName || "Store",
      service: s.service || "delivery",
    })),
  );

  const orderType = service.toLowerCase();

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  return (
    <>
      {/*
        Outer drawer: fixed inset-0 as a flex column — NOT overflow-y-auto.
        Using flex-col here (instead of overflow-y-auto + h-full on a child)
        avoids a Safari bug where h-full inside an overflow scroll container
        resolves to the content height rather than the viewport height, which
        left the drawer appearing almost blank with only the single store item
        visible far down the page.
      */}
      <div
        className={`fixed inset-0 bg-white z-40 flex flex-col transform transition-transform duration-300 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{ overscrollBehavior: "contain" }}
      >
        <div
          className={`flex flex-col flex-1 min-h-0 ${
            isScrolled ? "pt-[64px] md:pt-[126px]" : "pt-[80px] md:pt-[200px]"
          }`}
        >
          {/* Header Section */}
          <div className="page-container mt-4 pb-2 border-b border-gray-200 shrink-0">
            <h2 className="text-xl dominos-secondary text-gray-800 uppercase">
              Your Order
            </h2>
          </div>

          {/* Menu List */}
          <nav
            className="flex-1 overflow-y-auto page-container"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            <ul className="divide-y divide-gray-200">
              {/* Delivery/Collection Info */}
              {storeId && (
                <li>
                  <button
                    onClick={() => handleNavigation("/store-information")}
                    className="w-full text-left font-lato font-bold py-4 hover:bg-gray-50 transition-colors flex items-center justify-between"
                  >
                    <span className="text-dominos-blue">
                      {orderType === "delivery" ? "Delivery" : "Collection"}{" "}
                      from {storeName}
                    </span>
                    <svg
                      className="w-5 h-5 text-dominos-blue"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </li>
              )}
            </ul>
          </nav>
        </div>
      </div>
    </>
  );
}
